import { flexRender } from "@tanstack/react-table"
import { useVirtualizer } from "@tanstack/react-virtual"

import { useTableContext } from "#Contexts/TableContext"
import cn from "#Utils/cn"
import isModifiedClick from "#Utils/isModifiedClick"

const VirtualizedRows = ({ narrow = false, noBorder = false, isHighlighted }) => {
  const { tableContainerRef, table, error, dataSize } = useTableContext()

  const { rows } = table.getRowModel()

  // We need to calculate the row count including expanded rows
  const expandedRowsCount = rows.reduce((count, row) => {
    return count + (row.getIsExpanded() ? 1 : 0)
  }, 0)

  const rowVirtualizer = useVirtualizer({
    count: rows.length + expandedRowsCount,
    overscan: 10,
    getScrollElement: () => tableContainerRef?.current,
    estimateSize: () => 45,
  })

  const items = rowVirtualizer.getVirtualItems()
  const paddingTop = items.length > 0 ? items?.[0]?.start || 0 : 0
  const paddingBottom =
    items.length > 0 ? rowVirtualizer.getTotalSize() - (items?.[items.length - 1]?.end || 0) : 0

  const { renderSubComponent, onRowClick } = table.options.meta

  const highlighted = (row) => typeof isHighlighted === "function" && isHighlighted(row.original)

  // Opt-in whole-row click. Skip clicks on interactive elements so they keep their behavior.
  const handleRowClick = (row) => (event) => {
    if (event.target.closest("a, button, input, select, textarea, label, [role='checkbox']")) {
      return
    }
    // Leave modified clicks to the browser; don't hijack the end of a text selection.
    if (isModifiedClick(event) || !window.getSelection()?.isCollapsed) {
      return
    }
    onRowClick(row.original, event)
  }

  if (error) return null
  if (dataSize === 0) return null

  // Create a flattened array of all rows including expanded content
  const flattenedRows = []
  rows.forEach((row, index) => {
    flattenedRows.push({ type: "row", row, originalIndex: index })
    if (row.getIsExpanded()) {
      flattenedRows.push({ type: "expanded", row, originalIndex: index })
    }
  })

  return (
    <tbody>
      {paddingTop > 0 && (
        <tr>
          <td style={{ height: `${paddingTop}px` }} />
        </tr>
      )}
      {items.map((virtualRow) => {
        const item = flattenedRows[virtualRow.index]
        if (!item) return null

        const { row, type } = item

        if (type === "expanded") {
          return (
            <tr key={`${row.id}-expanded`}>
              <td colSpan={row.getVisibleCells().length} className="c-table__td">
                {renderSubComponent && renderSubComponent({ row })}
              </td>
            </tr>
          )
        }

        return (
          <tr
            key={row.id}
            onClick={onRowClick ? handleRowClick(row) : undefined}
            aria-selected={highlighted(row) || undefined}
            className={cn({
              "cursor-pointer hover:bg-surface-raised": onRowClick,
              "bg-surface-highlight": highlighted(row),
            })}
          >
            {row.getVisibleCells().map((cell) => {
              return (
                <td
                  key={cell.id}
                  className={cn(
                    "c-table__td",
                    cell.column.columnDef.meta?.className,
                    {
                      "border-0": noBorder,
                      "c-table__td--narrow": narrow,
                      "text-center": cell.column.id === "selection",
                    },
                  )}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              )
            })}
          </tr>
        )
      })}
      {paddingBottom > 0 && (
        <tr>
          <td style={{ height: `${paddingBottom}px` }} />
        </tr>
      )}
    </tbody>
  )
}

VirtualizedRows.propTypes = {
  narrow: PropTypes.bool,
  noBorder: PropTypes.bool,
  isHighlighted: PropTypes.func,
}

export default VirtualizedRows
